// The table artifact's client-side contract (#11727). Mirrors the backend
// validator's SHAPE (fp/agent/table_artifacts.py): a workbook of named
// sheets, each a typed schema plus rows keyed by column key. Values are
// validated for real on the server; the client parses defensively so a
// malformed document never renders as a silently empty grid, reads the
// original single-table shape as one sheet, and serializes exactly what the
// server expects back.

export type TableColumnType =
  | "text"
  | "number"
  | "date"
  | "boolean"
  | "record"
  | "link";
export const TABLE_COLUMN_TYPES: TableColumnType[] = [
  "text",
  "number",
  "date",
  "boolean",
  "record",
  "link",
];

// A number column's display format: how a reader is shown its cells, never
// what is stored. `currency` reads as US dollars with thousands separators
// and two decimals; the cell, the CSV and the citation matcher keep the
// number.
export type TableColumnFormat = "currency";

export interface TableColumn {
  // Identity: stable across relabels and retypes; rows are keyed by it.
  key: string;
  label: string;
  type: TableColumnType;
  format?: TableColumnFormat;
  // A link column's text: what each of its cells shows in place of the
  // address ("Agent Run"). The cell, the CSV and the API keep the address.
  link_text?: string;
}

// A `record` cell (#12430) names an RMS record: its public id (the identity),
// plus the kind and current name the server filled in so the grid can draw
// a link to the record without a lookup.
export interface TableRecordCell {
  public_id: string;
  kind?: string;
  label?: string;
}

export type TableCell = string | number | boolean | TableRecordCell | null;

export const isRecordCell = (value: TableCell): value is TableRecordCell =>
  value !== null &&
  typeof value === "object" &&
  typeof (value as TableRecordCell).public_id === "string";

/** What a record cell reads as in text: its name, or its id until named. */
export const recordCellLabel = (cell: TableRecordCell): string =>
  cell.label ?? cell.public_id;

const USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** A cell as the reader sees it: blanks as a dash, booleans as words, a
 *  currency column's numbers as dollars. */
export const displayCell = (
  value: TableCell,
  type: TableColumnType,
  format?: TableColumnFormat
): string => {
  if (value === null || value === "") return "—";
  // A record cell (#12430) reads as the record's name.
  if (isRecordCell(value)) return recordCellLabel(value);
  if (type === "boolean") {
    if (value === true || value === "true") return "Yes";
    if (value === false || value === "false") return "No";
  }
  if (format === "currency" && typeof value === "number") {
    return USD.format(value);
  }
  return String(value);
};

/** What a link cell shows: its column's link text, else the address's host
 *  (`app.finpilotai.com`), else the value as stored. */
export const linkCellText = (address: string, linkText?: string): string => {
  if (linkText) return linkText;
  try {
    return new URL(address).host || address;
  } catch {
    return address;
  }
};

// A cell's sources (#11975): column key -> the bracketed markers backing the
// value. One entry per row, aligned by index; `{}` for an unsourced row.
export type CellSources = Record<string, string[]>;

export interface TableSheet {
  name: string;
  columns: TableColumn[];
  rows: Record<string, TableCell>[];
  sources: CellSources[];
}

export interface TableArtifactContent {
  sheets: TableSheet[];
}

export const DEFAULT_SHEET_NAME = "Sheet 1";

const isColumnType = (value: unknown): value is TableColumnType =>
  typeof value === "string" && (TABLE_COLUMN_TYPES as string[]).includes(value);

const isColumnFormat = (value: unknown): value is TableColumnFormat =>
  value === "currency";

const isScalarCell = (value: unknown): value is TableCell =>
  value === null ||
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean";

const parseRecordCell = (value: unknown): TableRecordCell | null => {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  if (typeof raw.public_id !== "string" || !raw.public_id) return null;
  const cell: TableRecordCell = { public_id: raw.public_id };
  if (typeof raw.kind === "string" && raw.kind) cell.kind = raw.kind;
  if (typeof raw.label === "string" && raw.label) cell.label = raw.label;
  return cell;
};

const parseCellSources = (raw: unknown, keys: Set<string>): CellSources => {
  if (!raw || typeof raw !== "object") return {};
  const out: CellSources = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!keys.has(key) || !Array.isArray(value)) continue;
    const markers = value.filter(
      (marker): marker is string => typeof marker === "string" && marker !== ""
    );
    if (markers.length > 0) out[key] = markers;
  }
  return out;
};

const parseSheet = (raw: unknown, fallbackName: string): TableSheet => {
  const source = (raw && typeof raw === "object" ? raw : {}) as {
    name?: unknown;
    columns?: unknown;
    rows?: unknown;
    sources?: unknown;
  };
  const name =
    typeof source.name === "string" && source.name.trim()
      ? source.name
      : fallbackName;
  if (!Array.isArray(source.columns)) {
    throw new Error(`Sheet "${name}" has no columns list`);
  }
  const columns: TableColumn[] = source.columns.map((entry: unknown) => {
    const column = (entry ?? {}) as {
      key?: unknown;
      label?: unknown;
      type?: unknown;
      format?: unknown;
      link_text?: unknown;
    };
    if (typeof column.key !== "string" || !column.key) {
      throw new Error("A column is missing its key");
    }
    if (typeof column.label !== "string") {
      throw new Error(`Column ${column.key} is missing its label`);
    }
    if (!isColumnType(column.type)) {
      throw new Error(`Column ${column.key} has an unknown type`);
    }
    // Link text belongs to a link column; anywhere else it reads as none,
    // like an out-of-place format.
    if (column.type === "link") {
      return typeof column.link_text === "string" && column.link_text.trim()
        ? {
            key: column.key,
            label: column.label,
            type: column.type,
            link_text: column.link_text.trim(),
          }
        : { key: column.key, label: column.label, type: column.type };
    }
    // An unknown format, or one on a column that is not a number, reads as
    // none: the server refuses both, so it can only come from stale content.
    return column.type === "number" && isColumnFormat(column.format)
      ? {
          key: column.key,
          label: column.label,
          type: column.type,
          format: column.format,
        }
      : { key: column.key, label: column.label, type: column.type };
  });
  const rawRows: unknown[] = Array.isArray(source.rows) ? source.rows : [];
  const rows = rawRows.map((entry: unknown) => {
    const values = (entry && typeof entry === "object" ? entry : {}) as Record<
      string,
      unknown
    >;
    const row: Record<string, TableCell> = {};
    for (const column of columns) {
      const value = values[column.key];
      // A record column holds record objects and nothing else; every other
      // column holds scalars, so an object there reads as a blank.
      row[column.key] =
        column.type === "record"
          ? parseRecordCell(value)
          : value === undefined || !isScalarCell(value)
            ? null
            : value;
    }
    return row;
  });
  // The stored, aligned sources list; a missing or short list reads as
  // unsourced rows, never as a parse failure.
  const keys = new Set(columns.map((c) => c.key));
  const rawSources: unknown[] = Array.isArray(source.sources)
    ? source.sources
    : [];
  const sources = rows.map((_row, index) =>
    parseCellSources(rawSources[index], keys)
  );
  return { name, columns, rows, sources };
};

/** Parse the stored JSON; throws on anything that is not a table. */
export function parseTableContent(text: string): TableArtifactContent {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new Error("Table content is not valid JSON");
  }
  if (!raw || typeof raw !== "object") {
    throw new Error("Table content must be a {sheets} object");
  }
  const document = raw as { sheets?: unknown; columns?: unknown };
  if (Array.isArray(document.sheets)) {
    if (document.sheets.length === 0) {
      throw new Error("A table needs at least one sheet");
    }
    return {
      sheets: document.sheets.map((sheet: unknown, index: number) =>
        parseSheet(sheet, `Sheet ${index + 1}`)
      ),
    };
  }
  // The original single-table shape reads as one sheet.
  return { sheets: [parseSheet(raw, DEFAULT_SHEET_NAME)] };
}

/** The sources list as stored: aligned with the rows, and only present on
 *  a sheet that cites something — an unsourced table serializes exactly as
 *  it did before #11975. */
const serializeSheet = (sheet: TableSheet) => {
  const sources = sheet.rows.map((_row, index) => sheet.sources[index] ?? {});
  const cited = sources.some((cell) => Object.keys(cell).length > 0);
  return {
    name: sheet.name,
    // A column carries `format` or `link_text` only when it has one, so a
    // plain column is written exactly as before.
    columns: sheet.columns.map(({ key, label, type, format, link_text }) => ({
      key,
      label,
      type,
      ...(format ? { format } : {}),
      ...(link_text?.trim() ? { link_text: link_text.trim() } : {}),
    })),
    rows: sheet.rows,
    ...(cited ? { sources } : {}),
  };
};

export const serializeTableContent = (table: TableArtifactContent): string =>
  JSON.stringify({ sheets: table.sheets.map(serializeSheet) });

/** The markers a cell cites, in stored order; empty for an uncited cell. */
export const cellSources = (
  sheet: TableSheet,
  rowIndex: number,
  key: string
): string[] => sheet.sources[rowIndex]?.[key] ?? [];

/** The sheet with one cell's sources cleared — what a hand edit of the
 *  cell's value does (#11975): the value no longer comes from the source. */
export const withCellSourcesCleared = (
  sheet: TableSheet,
  rowIndex: number,
  key: string
): TableSheet => {
  const sources = sheet.rows.map((_row, index) => ({
    ...(sheet.sources[index] ?? {}),
  }));
  if (sources[rowIndex]) delete sources[rowIndex][key];
  return { ...sheet, sources };
};

const csvCell = (value: TableCell): string => {
  if (value === null) return "";
  const text = isRecordCell(value) ? recordCellLabel(value) : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** Labels as the header row, one line per row — the same shape the server's
 *  download route produces for one sheet. Kept client-side because the pane
 *  can download a PAST version, which only exists as an inline snapshot the
 *  download route does not serve. */
export function sheetToCsv(sheet: TableSheet): string {
  const lines = [sheet.columns.map((c) => csvCell(c.label)).join(",")];
  for (const row of sheet.rows) {
    lines.push(sheet.columns.map((c) => csvCell(row[c.key] ?? null)).join(","));
  }
  return lines.join("\n");
}

/** The sheet as tab-separated rows, header first (#12481): the shape a
 *  spreadsheet pastes as cells and a chat composer pastes as a table. A tab
 *  or a newline inside a value becomes a space, so the grid stays a grid. */
export function sheetToTsv(sheet: TableSheet): string {
  const cell = (value: TableCell | undefined): string =>
    value === null || value === undefined
      ? ""
      : String(value).replace(/[\t\n\r]+/g, " ");
  const lines = [sheet.columns.map((c) => cell(c.label)).join("\t")];
  for (const row of sheet.rows) {
    lines.push(sheet.columns.map((c) => cell(row[c.key])).join("\t"));
  }
  return lines.join("\n");
}

/** A fresh column key that no existing column uses: derived from the label
 *  the way the server expects keys to look (snake_case identifier). */
export function newColumnKey(label: string, existing: TableColumn[]): string {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .replace(/^[^a-z]/, "c$&") || "column";
  const taken = new Set(existing.map((c) => c.key));
  let key = base;
  let n = 2;
  while (taken.has(key)) {
    key = `${base}_${n}`;
    n += 1;
  }
  return key;
}

/** A sheet name no existing sheet uses (case-insensitively), Excel-style:
 *  "Sheet 2", "Sheet 3", … */
export function newSheetName(existing: TableSheet[]): string {
  const taken = new Set(existing.map((s) => s.name.toLowerCase()));
  let n = existing.length + 1;
  while (taken.has(`sheet ${n}`)) n += 1;
  return `Sheet ${n}`;
}

/** Client-side pre-check of a draft before it is sent: only what the user
 *  can fix in the pane — the server remains the authority. */
export function draftProblems(table: TableArtifactContent): string[] {
  const problems: string[] = [];
  if (table.sheets.length === 0) {
    problems.push("A table needs at least one sheet.");
  }
  const names = new Set<string>();
  table.sheets.forEach((sheet, sheetIndex) => {
    const name = sheet.name.trim().toLowerCase();
    if (!name) problems.push(`Sheet ${sheetIndex + 1} needs a name.`);
    else if (names.has(name))
      problems.push(`Two sheets are named "${sheet.name}".`);
    names.add(name);
    if (sheet.columns.length === 0) {
      problems.push(`Sheet "${sheet.name}" needs at least one column.`);
    }
    sheet.columns.forEach((column, index) => {
      if (!column.label.trim()) {
        problems.push(`Column ${index + 1} on "${sheet.name}" needs a label.`);
      }
    });
  });
  return problems;
}
